import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Text,
  Heading,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  useColorModeValue,
  Checkbox,
  Button,
  ButtonGroup,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
} from '@chakra-ui/react';
import fetchWithAuth from '../utils/fetchWithAuth';
import getCSRFtoken from '../utils/getCSRFtoken';

function TargetDemographicsForm({ populationId, audienceId, onChange }) {
  const [demographicFields, setDemographicFields] = useState([]);
  const [targetPercentages, setTargetPercentages] = useState({});
  const [enabledDemographics, setEnabledDemographics] = useState({});
  const [fieldPriority, setFieldPriority] = useState({});
  const [fieldTolerance, setFieldTolerance] = useState({});
  const [responseGoal, setResponseGoal] = useState(500);
  const [defaultTrancheSize, setDefaultTrancheSize] = useState(100);
  const [loading, setLoading] = useState(true);
  const [bucketSuggestions, setBucketSuggestions] = useState(null);
  const [currentBuckets, setCurrentBuckets] = useState({});
  const [applyingBuckets, setApplyingBuckets] = useState(false);

  const bgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  useEffect(() => {
    if (audienceId) {
      fetchDemographicFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId]);

  useEffect(() => {
    if (populationId) {
      fetchBucketConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationId]);

  useEffect(() => {
    // Calculate smart defaults when audience changes
    if (audienceId) {
      fetchAudienceAndCalculateDefaults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId]);

  const fetchAudienceAndCalculateDefaults = async () => {
    try {
      const response = await fetchWithAuth(`/api/audiences/${audienceId}/`);
      const audience = await response.json();
      const audienceSize = audience.subframe_size || 0;

      if (audienceSize > 0) {
        // Import the same calculation functions used in StagingPoll
        const { calculateSampleSize, calculateInitialTranche } = await import('../utils/sampleSize');
        const sampleSize = calculateSampleSize(audienceSize);
        const tranche = calculateInitialTranche(sampleSize);

        setResponseGoal(sampleSize);
        setDefaultTrancheSize(tranche);
      }
    } catch (error) {
      console.error('Error fetching audience for defaults:', error);
    }
  };

  const fetchBucketConfig = async () => {
    try {
      const response = await fetchWithAuth(`/api/populations/${populationId}/bucket-config/`);
      const data = await response.json();
      setCurrentBuckets(data.current || {});

      // Only show suggestions for fields not already configured
      const unconfigured = {};
      Object.keys(data.suggestions || {}).forEach(field => {
        if (!data.current || !data.current[field]) {
          unconfigured[field] = data.suggestions[field];
        }
      });
      if (Object.keys(unconfigured).length > 0) {
        setBucketSuggestions(unconfigured);
      }
    } catch (error) {
      console.error('Error fetching bucket config:', error);
    }
  };

  const applyBucketDefaults = async () => {
    setApplyingBuckets(true);
    try {
      const merged = { ...currentBuckets, ...bucketSuggestions };
      const csrfToken = getCSRFtoken();
      await fetchWithAuth(`/api/populations/${populationId}/bucket-config/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ bucket_config: merged }),
      });
      setCurrentBuckets(merged);
      setBucketSuggestions(null);
      // Re-fetch demographics to include bucketed fields
      await fetchDemographicFields();
    } catch (error) {
      console.error('Error applying bucket config:', error);
    }
    setApplyingBuckets(false);
  };

  useEffect(() => {
    // Notify parent component of changes
    // Convert to structure: {fieldName: {enabled, priority, tolerance, targets}}
    if (onChange) {
      const formattedDemographics = {};
      Object.keys(targetPercentages).forEach(fieldName => {
        formattedDemographics[fieldName] = {
          enabled: enabledDemographics[fieldName] !== undefined ? enabledDemographics[fieldName] : true,
          priority: fieldPriority[fieldName] !== undefined ? fieldPriority[fieldName] : 1.0,
          tolerance: fieldTolerance[fieldName] !== undefined ? fieldTolerance[fieldName] : 0,
          targets: targetPercentages[fieldName],
        };
      });

      onChange({
        target_demographics: formattedDemographics,
        response_goal: responseGoal,
        default_tranche_size: defaultTrancheSize,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPercentages, enabledDemographics, fieldPriority, fieldTolerance, responseGoal, defaultTrancheSize]);

  const fetchDemographicFields = async () => {
    try {
      // Fetch demographic distribution for the specific audience
      // This respects subframe_filters and max_pool_size
      const response = await fetchWithAuth(`/api/audiences/${audienceId}/demographics/`);
      const data = await response.json();

      if (data.demographic_breakdowns && Object.keys(data.demographic_breakdowns).length > 0) {
        const demographicBreakdowns = data.demographic_breakdowns;

        // Backend already filtered fields - just convert API format to internal format
        const fieldCounts = {}; // We'll use percentages directly from API
        const fieldsToUse = [];

        Object.keys(demographicBreakdowns).forEach(fieldName => {
          fieldCounts[fieldName] = {};
          const values = [];

          const breakdown = demographicBreakdowns[fieldName];
          Object.keys(breakdown).forEach(value => {
            values.push(value);
            // Store the percentage directly (API already did smart rounding)
            fieldCounts[fieldName][value] = breakdown[value];
          });

          // Add to fields list (backend already filtered, no need to filter again)
          fieldsToUse.push({
            name: fieldName,
            values: values,
          });
        });

        setDemographicFields(fieldsToUse);

        // Initialize with ACTUAL audience distribution (already calculated by backend)
        const initialPercentages = {};
        const initialEnabled = {};
        const initialPriority = {};
        const initialTolerance = {};
        fieldsToUse.forEach(field => {
          initialPercentages[field.name] = {};
          initialEnabled[field.name] = false; // All demographics disabled by default
          initialPriority[field.name] = 1.0;  // Standard correction
          initialTolerance[field.name] = 5;   // 5 percentage points tolerance by default

          field.values.forEach(value => {
            // Use the percentage directly from the API (already smart-rounded to sum to 100%)
            initialPercentages[field.name][value] = fieldCounts[field.name][value] || 0;
          });
        });

        setTargetPercentages(initialPercentages);
        setEnabledDemographics(initialEnabled);
        setFieldPriority(initialPriority);
        setFieldTolerance(initialTolerance);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching demographic fields:', error);
      setLoading(false);
    }
  };

  const handlePercentageChange = (fieldName, value, percentage) => {
    setTargetPercentages(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [value]: parseFloat(percentage) || 0,
      },
    }));
  };

  const handleToggleEnabled = (fieldName) => {
    setEnabledDemographics(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleSelectAll = () => {
    const updated = {};
    demographicFields.forEach(field => { updated[field.name] = true; });
    setEnabledDemographics(updated);
  };

  const handleDeselectAll = () => {
    const updated = {};
    demographicFields.forEach(field => { updated[field.name] = false; });
    setEnabledDemographics(updated);
  };

  const calculateTotal = (fieldName) => {
    if (!targetPercentages[fieldName]) return 0;
    return Object.values(targetPercentages[fieldName]).reduce((sum, val) => sum + val, 0);
  };

  const isFieldValid = (fieldName) => {
    const total = calculateTotal(fieldName);
    return Math.abs(total - 100) < 0.1; // Allow for small floating point errors
  };

  if (loading) {
    return <Text>Analyzing population demographics...</Text>;
  }

  if (demographicFields.length === 0) {
    return (
      <Alert status="warning">
        <AlertIcon />
        No demographic data found in population
      </Alert>
    );
  }

  return (
    <Box bg={bgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        <Heading as="h3" size="md">
          Target Demographics Configuration
        </Heading>

        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm" fontWeight="bold">
              Default values show the current population distribution.
            </Text>
            <Text fontSize="sm" mt={1}>
              Use checkboxes to select which demographics should be weighted in tranche generation.
              Only enabled demographics will be used to balance your sample.
            </Text>
            <Text fontSize="sm" mt={1}>
              Adjust percentages to set your target demographic goals for enabled fields.
              Use priority and tolerance to control how aggressively each dimension is corrected.
            </Text>
          </Box>
        </Alert>

        {/* Bucket suggestions for high-cardinality numeric fields */}
        {bucketSuggestions && Object.keys(bucketSuggestions).length > 0 && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <Text fontSize="sm" fontWeight="bold">
                Numeric fields detected
              </Text>
              <Text fontSize="sm" mt={1}>
                {Object.entries(bucketSuggestions).map(([field, config]) => (
                  <span key={field}>
                    <strong>{field}</strong> has many unique values and can be grouped into ranges
                    ({config.ranges.map(r => r.label).join(', ')}).{' '}
                  </span>
                ))}
              </Text>
              <Button
                size="sm"
                colorScheme="blue"
                mt={2}
                onClick={applyBucketDefaults}
                isLoading={applyingBuckets}
              >
                Apply Grouping
              </Button>
            </Box>
          </Alert>
        )}

        {/* Response Goal and Tranche Size */}
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Response Goal</FormLabel>
            <NumberInput
              value={responseGoal}
              onChange={(valueString) => setResponseGoal(parseInt(valueString) || 0)}
              min={1}
              max={100000}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel>Default Tranche Size</FormLabel>
            <NumberInput
              value={defaultTrancheSize}
              onChange={(valueString) => setDefaultTrancheSize(parseInt(valueString) || 0)}
              min={1}
              max={10000}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </HStack>

        {/* Select All / Deselect All */}
        <HStack justify="flex-end">
          <ButtonGroup size="sm" variant="outline">
            <Button onClick={handleSelectAll}>Select All</Button>
            <Button onClick={handleDeselectAll}>Deselect All</Button>
          </ButtonGroup>
        </HStack>

        {/* Demographic Fields */}
        {demographicFields.map(field => (
          <Box
            key={field.name}
            p={4}
            bg="white"
            borderRadius="md"
            border="2px solid"
            borderColor={enabledDemographics[field.name] ? 'blue.400' : borderColor}
            opacity={enabledDemographics[field.name] ? 1 : 0.6}
          >
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" align="flex-start">
                <HStack spacing={3}>
                  <Checkbox
                    isChecked={enabledDemographics[field.name]}
                    onChange={() => handleToggleEnabled(field.name)}
                    colorScheme="blue"
                    size="lg"
                  />
                  <VStack align="start" spacing={0}>
                    <Heading as="h4" size="sm" textTransform="capitalize">
                      {field.name.replace(/_/g, ' ')}
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      {enabledDemographics[field.name]
                        ? 'Enabled - will be weighted in tranche generation'
                        : 'Disabled - for reference only'}
                    </Text>
                  </VStack>
                </HStack>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={isFieldValid(field.name) ? 'green.500' : 'red.500'}
                >
                  Total: {calculateTotal(field.name).toFixed(1)}%
                  {isFieldValid(field.name) ? ' ✓' : ' ✗'}
                </Text>
              </HStack>

              {field.values.map(value => (
                <HStack key={value} spacing={4}>
                  <Text flex="1" fontSize="sm">
                    {value}:
                  </Text>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    width="100px"
                    value={targetPercentages[field.name]?.[value] || 0}
                    onChange={(e) => handlePercentageChange(field.name, value, e.target.value)}
                  />
                  <Text fontSize="sm" width="20px">%</Text>
                </HStack>
              ))}

              {!isFieldValid(field.name) && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  Percentages must sum to 100%
                </Alert>
              )}

              {/* Priority and Tolerance controls — only shown when enabled */}
              {enabledDemographics[field.name] && (
                <Box pt={2} borderTop="1px solid" borderColor="gray.200">
                  <HStack spacing={6} align="flex-start">
                    <FormControl flex="1">
                      <FormLabel fontSize="xs" color="gray.600" mb={1}>Priority</FormLabel>
                      <Select
                        size="sm"
                        value={fieldPriority[field.name] ?? 1.0}
                        onChange={(e) => setFieldPriority(prev => ({
                          ...prev,
                          [field.name]: parseFloat(e.target.value),
                        }))}
                      >
                        <option value={0.5}>Low — gentle nudge</option>
                        <option value={1.0}>Standard</option>
                        <option value={2.0}>High — aggressive correction</option>
                      </Select>
                    </FormControl>
                    <FormControl flex="1">
                      <FormLabel fontSize="xs" color="gray.600" mb={1}>
                        Tolerance: {fieldTolerance[field.name] ?? 5}pp
                      </FormLabel>
                      <Slider
                        min={0}
                        max={15}
                        step={1}
                        value={fieldTolerance[field.name] ?? 5}
                        onChange={(val) => setFieldTolerance(prev => ({
                          ...prev,
                          [field.name]: val,
                        }))}
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                      <HStack justify="space-between" mt={0}>
                        <Text fontSize="xs" color="gray.400">Exact</Text>
                        <Text fontSize="xs" color="gray.400">Loose</Text>
                      </HStack>
                    </FormControl>
                  </HStack>
                </Box>
              )}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

export default TargetDemographicsForm;
